// sendRequest.js
import axiosModule from '@/common/axiosModule';
import { logDebug, logError } from '@/utils/logHelper';

/**
 * @typedef {Object} RequestOptions
 * @property {string} method - "GET" | "POST" | "POST_MULTIPART" | "PUT" | "DELETE"
 * @property {Object=} data - request body (POST, PUT 등에 사용)
 * @property {Object=} params - 쿼리 파라미터 (GET 등에 사용)
 * @property {FormData=} formData - 멀티파트 전송 시 사용
 */

/**
 * 하나의 함수에서 모든 요청을 처리하는 공용 메소드
 * @param {string} url - 요청할 주소
 * @param {RequestOptions} options - 요청 옵션
 * @returns {Promise<any>} 서버 응답 data
 */
export async function sendRequest(url, options = {}) {
  const { method = 'GET', data, params, formData } = options;

  try {
    let response;

    // method(대소문자 모두 허용을 위해 toUpperCase 처리)
    switch (method.toUpperCase()) {
      case 'GET':
        response = await axiosModule.get(url, params);
        break;

      case 'POST':
        // 일반 POST
        response = await axiosModule.post(url, data);
        break;

      case 'POST_MULTIPART':
        // formData 전송
        response = await axiosModule.postMultipart(url, formData);
        break;

      case 'POST_BLOB':
        response = await axiosModule.postBlob(url, data);
        break;

      case 'GET_BLOB':
        response = await axiosModule.instance
          ? axiosModule.instance.get(url, { responseType: 'blob' })
          : axiosModule.get(url, {}); // fallback
        break;
      case 'PUT':
        response = await axiosModule.put(url, data);
        break;

      case 'PATCH':
        response = await axiosModule.patch(url, data);
        break;

      case 'PATCH_MULTIPART':
        response = await axiosModule.patchMultipart(url, formData);
        break;

      case 'DELETE':
        response = await axiosModule.delete(url);
        break;

      default:
        throw new Error(`지원하지 않는 method입니다: ${method}`);
    }

    // 응답이 정상이라면(Interceptor에서 SUCCESS 상태) 여기서 response = { status, message, data } 등
    logDebug(`Request to [${url}] success. response:`, response);
    return response;
  } catch (error) {
    // Interceptor에서 FAILURE/ERROR 시 throw된 에러도 여기서 잡힘
    logError(`Request to [${url}] failed:`, error);
    // 여기서 별도 처리(토스트 알림 등) 후 재전달
    throw error;
  }
}
